import { endOfMonth, format, startOfMonth, subMonths } from 'date-fns';
import { CalcBuilderComment } from 'src/models/calculation-builder/CalcBuilderComment';
import { DataSourceRecord, getNewDataSourceRecord } from 'src/models/common/DataSourceRecord';
import { PullBalancePayload } from 'src/models/calculation-builder/PullBalanceRequest';
import { PullBalanceWSMessage, PullCDTValueMessage } from 'src/models/calculation-builder/PullBalanceWebSocketMessage';
import CONSTANTS from 'src/utils/constants';
import StringUtils from 'src/utils/stringUtils';
import TPEAction from '../../models/common/TPEAction';
import { DataSourcesState } from './DataSourcesState';

/**
 * List here the actions supported by this reducer
 */
export const ACTIONS = Object.freeze({
    SET_RECORDS: new TPEAction('SET_RECORDS'),
    ADD_NEW_RECORD: new TPEAction('ADD_NEW_RECORD'),
    EDIT_RECORD: new TPEAction('EDIT_RECORD'),
    CANCEL_EDITING: new TPEAction('CANCEL_EDITING'),
    UPDATE_RECORD_FIELD: new TPEAction('UPDATE_RECORD_FIELD'),
    TOGGLE_SHOW_DECIMALS: new TPEAction('TOGGLE_SHOW_DECIMALS'),
    TOGGLE_PULL_BALANCE_BREAKDOWNS: new TPEAction('TOGGLE_PULL_BALANCE_BREAKDOWNS'),
    SHOW_DATA_KEY_SELECTION: new TPEAction('SHOW_DATA_KEY_SELECTION'),
    ADD_COMMENT: new TPEAction('ADD_COMMENT'),
    DELETE_RECORD: new TPEAction('DELETE_RECORD'),
    DUPLICATE_RECORD: new TPEAction('DUPLICATE_RECORD'),
    SAVE_RECORD: new TPEAction('SAVE_RECORD'),
    SET_COA_TYPES: new TPEAction('SET_COA_TYPES'),
    SET_DATA_KEY_INPUT: new TPEAction('SET_DATA_KEY_INPUT'),
    SET_DATA_KEY_COA_INPUT: new TPEAction('SET_DATA_KEY_COA_INPUT'),
    REMOVE_DATA_KEY_COA_INPUT: new TPEAction('REMOVE_DATA_KEY_COA_INPUT'),
    REMOVE_UNUSED_DATA_KEY_COA_INPUTS: new TPEAction('REMOVE_UNUSED_DATA_KEY_COA_INPUTS'),
    EDIT_COA: new TPEAction('EDIT_COA'),
    UPDATE_BALANCES: new TPEAction('UPDATE_BALANCES'),
    UPDATE_CDT_VALUE: new TPEAction('UPDATE_CDT_VALUE'),
    UPDATE_MDA_RECORD: new TPEAction('UPDATE_MDA_RECORD'),
    TOGGLE_CATEGORY_EXPANSION: new TPEAction('TOGGLE_CATEGORY_EXPANSION'),
    TOGGLE_ACCOUNT_EXPANSION: new TPEAction('TOGGLE_ACCOUNT_EXPANSION'),
    SELECT_DRILLDOWN_COA: new TPEAction('SELECT_DRILLDOWN_COA'),
    SET_RECORD_ID_FOR_ACTION: new TPEAction('SET_RECORD_ID_FOR_ACTION'),
    SET_ERRORS: new TPEAction('SET_ERRORS'),
    CLEAR_ERRORS: new TPEAction('CLEAR_ERRORS'),
    SHOW_EDIT_CONFIRM_MODAL: new TPEAction('SHOW_EDIT_CONFIRM_MODAL'),
    HIDE_EDIT_CONFIRM_MODAL: new TPEAction('HIDE_EDIT_CONFIRM_MODAL'),
    SHOW_DUPLICATE_CONFIRM_MODAL: new TPEAction('SHOW_DUPLICATE_CONFIRM_MODAL'),
    HIDE_DUPLICATE_CONFIRM_MODAL: new TPEAction('HIDE_DUPLICATE_CONFIRM_MODAL'),
    SHOW_DELETE_CONFIRM_MODAL: new TPEAction('SHOW_DELETE_CONFIRM_MODAL'),
    HIDE_DELETE_CONFIRM_MODAL: new TPEAction('HIDE_DELETE_CONFIRM_MODAL'),
    SHOW_STEP3_CONFIRM_MODAL: new TPEAction('SHOW_STEP3_CONFIRM_MODAL'),
    HIDE_STEP3_CONFIRM_MODAL: new TPEAction('HIDE_STEP3_CONFIRM_MODAL'),
    CLEAR_SAVED_RECORD_ID: new TPEAction('CLEAR_SAVED_RECORD_ID'),
    SET_NEW_COMMENT_PAYLOAD: new TPEAction('SET_NEW_COMMENT_PAYLOAD'),
    SET_COMMENTS_HISTORY: new TPEAction('SET_COMMENTS_HISTORY'),
    CLEAR_UNSAVED_COMMENTS: new TPEAction('CLEAR_UNSAVED_COMMENTS'),
    REQUEST_BALANCE_PULL: new TPEAction('REQUEST_BALANCE_PULL'),
    SET_BALANCE_PULL_IN_PROGRESS: new TPEAction('SET_BALANCE_PULL_IN_PROGRESS'),
    SET_PULL_BALANCE_ERROR: new TPEAction('SET_PULL_BALANCE_ERROR'),
    SET_BALANCE_DRILL_DOWN: new TPEAction('SET_BALANCE_DRILL_DOWN'),
    ADD_PULL_BALANCE_PAYLOAD: new TPEAction('ADD_PULL_BALANCE_PAYLOAD'),
    REMOVE_PULL_BALANCE_PAYLOAD: new TPEAction('REMOVE_PULL_BALANCE_PAYLOAD'),
    UPDATE_DATASOURCE_RECORD_STATUS: new TPEAction('UPDATE_DATASOURCE_RECORD_STATUS'),
    SHOW_UPDATE_ALL_BALANCES_CONFIRM_MODAL: new TPEAction('SHOW_UPDATE_ALL_BALANCES_CONFIRM_MODAL'),
    HIDE_UPDATE_ALL_BALANCES_CONFIRM_MODAL: new TPEAction('HIDE_UPDATE_ALL_BALANCES_CONFIRM_MODAL'),
    PROCESS_NEXT_BALANCE_PULL_REQUEST: new TPEAction('PROCESS_NEXT_BALANCE_PULL_REQUEST'),
    TOGGLE_DATA_KEY_VIEW: new TPEAction('TOGGLE_DATA_KEY_VIEW'),
    SET_FX_INPUTS: new TPEAction('SET_FX_INPUTS'),
    SET_SHOW_CDT_SELECTOR: new TPEAction('SET_SHOW_CDT_SELECTOR'),
    SET_SELECTED_CUSTOM_COA: new TPEAction('SET_SELECTED_CUSTOM_COA'),
    SET_CUSTOM_COA_REFERENCE: new TPEAction('SET_CUSTOM_COA_REFERENCE'),
    SET_SAVE_ERROR: new TPEAction('SET_SAVE_ERROR'),
    SHOW_STANDARD_ALLOCATION_MODAL: new TPEAction('SHOW_STANDARD_ALLOCATION_MODAL'),
    SHOW_STANDARD_ALLOCATION_DELETE_CONFIRM_MODAL: new TPEAction('SHOW_STANDARD_ALLOCATION_DELETE_CONFIRM_MODAL'),
    HIDE_STANDARD_ALLOCATION_DELETE_CONFIRM_MODAL: new TPEAction('HIDE_STANDARD_ALLOCATION_DELETE_CONFIRM_MODAL'),
    DELETE_STANDARD_ALLOCATION_RECORD: new TPEAction('DELETE_STANDARD_ALLOCATION_RECORD'),
    TOGGLE_PASS_THROUGH_FLAG: new TPEAction('TOGGLE_PASS_THROUGH_FLAG'),
    CLEAR_ALL_BALANCE_PULL_REQUESTS: new TPEAction('CLEAR_ALL_BALANCE_PULL_REQUESTS')
});

function clearBalancesAndAbandonPullRequest(editingRecord: any) {
    const isInProgress = editingRecord['balancePullInProgress'] || false;
    if (isInProgress) {
        editingRecord['balancePullInProgress'] = false;
        const abandonedBalancePullRequests = editingRecord['abandonedBalancePullRequests'] || [] as string[];
        const balancePullingTask = editingRecord['balancePullingTask'] as PullBalanceWSMessage;
        if (balancePullingTask != null) {
            abandonedBalancePullRequests.push(balancePullingTask.requestId);
        } else if (editingRecord['unsavedBalancePullRequestID'] != null) {
            abandonedBalancePullRequests.push(editingRecord['unsavedBalancePullRequestID']);
        }
        editingRecord['abandonedBalancePullRequests'] = abandonedBalancePullRequests;
    }
    clearBalances(editingRecord);
}

function isBalancePullAbandoned(editingRecord: any, balancePullRequestID: string) {
    const abandonedBalancePullRequests = editingRecord['abandonedBalancePullRequests'] || [] as string[];
    const unsavedBalancePullRequestID = editingRecord['unsavedBalancePullRequestID'];

    return abandonedBalancePullRequests.includes(balancePullRequestID) || (unsavedBalancePullRequestID != null && abandonedBalancePullRequests.includes(unsavedBalancePullRequestID));
}

function clearBalances(editingRecord: any) {
    editingRecord['requestBalancePull'] = false;
    editingRecord['glBalances'] = null;
    editingRecord['glBalanceDrillDownKeys'] = null;
    editingRecord['glBalanceDrillDown'] = undefined;
    editingRecord['value'] = null;
    editingRecord['lastBalancePullRequestID'] = null as unknown as string;
    editingRecord['lastBalancePullDate'] = null as unknown as number;
}

function clearBalanceDrillDown(editingRecord: any) {
    editingRecord['glBalanceDrillDown'] = undefined;
    editingRecord['drillDownCOASelected'] = undefined;
    editingRecord['expandedCategories'] = undefined;
}

function getSelectedRecordAndParams(payload: any, records: DataSourceRecord[], checkUnsaved: boolean = false) {
    const { recordID, value = null } = payload;
    let selectedRecord = null;
    if (recordID != null) {
        selectedRecord = records.find(x => x.datasourceId == recordID);
        if (selectedRecord == null && checkUnsaved != null && checkUnsaved) {
            selectedRecord = records.find(x => x.unsavedDatasourceId == recordID);   
        }
    }
    return { selectedRecord: selectedRecord || null, value };
}

function getSelectedRecordByRequestID(requestID: string, records: DataSourceRecord[]) {
    const selectedRecord = requestID != null ? records.find(x => x.balancePullingTask?.requestId == requestID) : null;
    return selectedRecord || null;
}

function backupBalanceData(editingRecord: any) {
    editingRecord['originalBalanceData'] = {
        value: editingRecord['value'],
        fxRate: editingRecord['fxRate'],
        glBalances: editingRecord['glBalances'],
        glBalanceDrillDownKeys: editingRecord['glBalanceDrillDownKeys'],
        lastBalancePullRequestID: editingRecord['lastBalancePullRequestID'],
        lastBalancePullDate: editingRecord['lastBalancePullDate']
    }
}

function restoreFromBackupBalanceData(editingRecord: any) {
    const originalBalanceData = editingRecord['originalBalanceData'];
    if (originalBalanceData != null) {
        editingRecord['value'] = originalBalanceData.value;
        editingRecord['fxRate'] = originalBalanceData.fxRate;
        editingRecord['glBalances'] = originalBalanceData.glBalances;
        editingRecord['glBalanceDrillDownKeys'] = originalBalanceData.glBalanceDrillDownKeys;
        editingRecord['lastBalancePullRequestID'] = originalBalanceData.lastBalancePullRequestID;
        editingRecord['lastBalancePullDate'] = originalBalanceData.lastBalancePullDate;
    }
    editingRecord['originalBalanceData'] = null;
}

/**  
* This function is responsible for updating the state based on action type
* @param state The current dashboard state
* @param action The current dispatched action 
*/
export function dataSourcesReducer(state: DataSourcesState, action: TPEAction): DataSourcesState {
    switch (action.type) {
        case ACTIONS.SET_RECORDS.type: {
            const records = action.payload;
            return {
                ...state,
                loading: false,
                dataSourceRecords: records,
                recordBeingEdited: undefined,
            }
        }
        case ACTIONS.ADD_NEW_RECORD.type: {
            const newRecord = getNewDataSourceRecord(state.recordIdCounter);
            newRecord.unsavedDatasourceId = newRecord.datasourceId;
            newRecord.calculationNumber = action.payload;
            const updatedDataSourceRecords = [...state.dataSourceRecords, newRecord];
            if (state.recordBeingEdited != null) {
                clearBalanceDrillDown(state.recordBeingEdited);
            }
            return {
                ...state,
                loading: false,
                dataSourceRecords: updatedDataSourceRecords,
                recordIdCounter: state.recordIdCounter + 1,
                recordBeingEdited: newRecord, //New record is in edit mode
                savedRecordId: undefined,
            }
        }
        case ACTIONS.EDIT_RECORD.type: {
            const records = [...state.dataSourceRecords];
            const selectedRecordArr = records.filter(x => x.datasourceId == state.recordIdForAction);
            if (selectedRecordArr.length > 0) {
                const selectedRecord = selectedRecordArr[0];
                const selectedRecordCopy = {...selectedRecord, balancePullInProgress: false};
                restoreFromBackupBalanceData(selectedRecordCopy);
                selectedRecord.isEditing = true;
                selectedRecord.showDataKeySelection = true;
                return {
                    ...state,
                    recordBeingEdited: selectedRecord,
                    dataSourceRecords: records,
                    recordBeingEditedOriginal: selectedRecordCopy,
                    savedRecordId: undefined,
                }
            } else {
                return state;
            }
        }
        case ACTIONS.DUPLICATE_RECORD.type: {
            const records = [...state.dataSourceRecords];
            const selectedRecordArr = records.filter(x => x.datasourceId == state.recordIdForAction);
            if (selectedRecordArr.length > 0) {
                const selectedRecord = selectedRecordArr[0];
                const selectedRecordCopy = {
                    ...selectedRecord, 
                    datasourceId: String(state.recordIdCounter), 
                    description: CONSTANTS.DUPLICATE_DATA_SOURCE_DESC_PREFIX + selectedRecord.description,
                    isNewRecord: true,
                    isEditing: true,
                    showDataKeySelection: true,
                    requestBalancePull: false,
                    balancePullInProgress: false,
                    balancePullingTask: undefined,
                    abandonedBalancePullRequests: [],
                    errors: undefined,
                    pullBalanceError: undefined
                };
                if (selectedRecord.dataKeyInput != null) {
                    selectedRecordCopy.dataKeyInput = {...selectedRecord.dataKeyInput};
                    if (selectedRecord.dataKeyInput.selectedCOA != null) {
                        selectedRecordCopy.dataKeyInput.selectedCOA = new Map(selectedRecord.dataKeyInput.selectedCOA);
                    }
                }
                if (selectedRecord.multiAllocationWorksheetValueIdentifier != null) {
                    selectedRecordCopy.multiAllocationWorksheetValueIdentifier = {
                        ...selectedRecord.multiAllocationWorksheetValueIdentifier
                    };
                }
                clearBalances(selectedRecordCopy);
                clearBalanceDrillDown(selectedRecordCopy);

                records.push(selectedRecordCopy);
                return {
                    ...state,
                    recordBeingEdited: selectedRecordCopy,
                    dataSourceRecords: records,
                    recordIdCounter: state.recordIdCounter + 1,
                    savedRecordId: undefined,
                }
            } else {
                return state;
            }
        }
        case ACTIONS.DELETE_RECORD.type: {
            const records = state.dataSourceRecords.filter(x => x.datasourceId != state.recordIdForAction);
            if (state.recordBeingEdited != undefined && state.recordBeingEdited.datasourceId == state.recordIdForAction) {
                return {
                    ...state,
                    dataSourceRecords: records,
                    recordBeingEdited: undefined,
                    savedRecordId: undefined,
                };
            } else {
                return {
                    ...state,
                    dataSourceRecords: records,
                    savedRecordId: undefined,
                };
            }
        }
        case ACTIONS.CANCEL_EDITING.type: {
            const editingRecord = state.recordBeingEdited;
            if (editingRecord?.isNewRecord) {
                // Delete the record if it is a new record
                const records = state.dataSourceRecords.filter(x => x.datasourceId != editingRecord.datasourceId);
                return {
                    ...state,
                    dataSourceRecords: records,
                    recordBeingEdited: undefined,
                }
            } else {
                const index = state.dataSourceRecords.indexOf(editingRecord || {} as DataSourceRecord);
                const records = [...state.dataSourceRecords];
                if (index >= 0 && state.recordBeingEditedOriginal != null) {
                    if(editingRecord?.datasource === CONSTANTS.DATA_SOURCE_TYPES.GENERAL_LEDGER) { // this might not be needed if we use the same pulling api
                        clearBalancesAndAbandonPullRequest(state.recordBeingEdited);
                        state.recordBeingEditedOriginal.abandonedBalancePullRequests = state.recordBeingEdited?.abandonedBalancePullRequests as string[];
                        state.recordBeingEditedOriginal.unsavedBalancePullRequestID = state.recordBeingEdited?.unsavedBalancePullRequestID as string;
                    }
                    records[index] = state.recordBeingEditedOriginal;
                }
                return {
                    ...state,
                    recordBeingEdited: undefined,
                    recordBeingEditedOriginal: undefined,
                    dataSourceRecords: records,
                }
            }
        }
        case ACTIONS.SAVE_RECORD.type: {
            const { recordBeingEdited, unsavedComments } = state;

            // This is the object coming from the web API response
            const { recordID, updatedRecord } = action.payload;

            const records = [...state.dataSourceRecords];
            const { selectedRecord } = getSelectedRecordAndParams(action.payload, records);
            if (selectedRecord != null) {
                selectedRecord.datasourceId = updatedRecord.datasourceId;
                selectedRecord.currency = updatedRecord.currency;
                selectedRecord.isNewRecord = false;
                selectedRecord.isEditing = false;
                selectedRecord.showDataKeySelection = false;
                selectedRecord.dataKey = ((selectedRecord.dataKeyInput?.selectedCOA.size || 0) + 2) + CONSTANTS.DATA_SOURCE_DATA_KEY_COUNT_SUFFIX; // Add 2 for company and account which are mandatory
                selectedRecord.errors = undefined;
                selectedRecord.requestAutoSave = false;
                selectedRecord.originalBalanceData = undefined;
                clearBalanceDrillDown(selectedRecord);
                if (selectedRecord.datasource === CONSTANTS.DATA_SOURCE_TYPES.CUSTOM_DATA_TABLE){
                    selectedRecord.customDatasourceCalculationNumber = updatedRecord.customDatasourceCalculationNumber;
                    selectedRecord.customDatasourceTableId = updatedRecord.customDatasourceTableId;
                    selectedRecord.customDatasourceValueId = updatedRecord.customDatasourceValueId;
                    selectedRecord.customPeriod = updatedRecord.startPeriod;
                    selectedRecord.dataKey = updatedRecord.customDatasourceCalculationNumber;
                }

                if (selectedRecord.datasource === CONSTANTS.DATA_SOURCE_TYPES.TP_ALLOCATION_WORKSHEET) {
                    selectedRecord.dataKey = updatedRecord.tpAllocationWorksheetName;
                    selectedRecord.value = updatedRecord.value;
                    selectedRecord.errors = updatedRecord.errors == null ? undefined : new Map(Object.entries(updatedRecord.errors));
                    selectedRecord.tpAllocationWorksheetName  = updatedRecord.tpAllocationWorksheetName;
                    selectedRecord.tpAllocationGroupName = updatedRecord.tpAllocationGroupName;
                    selectedRecord.tpAllocationFormulaName = updatedRecord.tpAllocationFormulaName;
                }

                if (selectedRecord.datasource === CONSTANTS.DATA_SOURCE_TYPES.MULTIDIMENSIONAL_ALLOCATION_WORKSHEET) {
                    selectedRecord.dataKey = updatedRecord.multiAllocationWorksheetName || '';
                    selectedRecord.value = updatedRecord.value;
                    selectedRecord.errors = updatedRecord.errors == null ? undefined : new Map(Object.entries(updatedRecord.errors));
                    selectedRecord.multiAllocationWorksheetId = updatedRecord.multiAllocationWorksheetId;
                    selectedRecord.multiAllocationWorksheetVersion = updatedRecord.multiAllocationWorksheetVersion;
                    selectedRecord.multiAllocationWorksheetName = updatedRecord.multiAllocationWorksheetName;
                    selectedRecord.multiAllocationWorksheetDatasourceId = updatedRecord.multiAllocationWorksheetDatasourceId;
                    selectedRecord.multiAllocationWorksheetValueIdentifier = updatedRecord.multiAllocationWorksheetValueIdentifier;
                    selectedRecord.multiAllocationWorksheetDatasourceResultsFileURL = updatedRecord.multiAllocationWorksheetDatasourceResultsFileURL;
                }
                

                // Updating unsaved comments if present with the new dataSource ID (only if saved from data key container)
                if (recordBeingEdited != null && recordBeingEdited.datasourceId == selectedRecord.datasourceId) {
                    unsavedComments.forEach( uc => {
                        uc.datasourceId = selectedRecord.datasourceId;
                    });
                    return {
                        ...state,
                        recordBeingEdited: undefined,
                        recordBeingEditedOriginal: undefined,
                        savedRecordId: selectedRecord.datasourceId,
                        dataSourceRecords: records,
                    }
                } else {
                    return {
                        ...state,
                        dataSourceRecords: records,
                    }
                }
                
            } else {
                return state;
            }

        }
        case ACTIONS.UPDATE_RECORD_FIELD.type:
            const editingRecord = state.recordBeingEdited as any;
            const { fieldName, value } = action.payload;

            if (fieldName != CONSTANTS.DATA_SOURCE_FIELDS.DESCRIPTION.ACCESSOR && editingRecord[fieldName] != value) {
                clearBalancesAndAbandonPullRequest(editingRecord);
            }
            // Clear the custom period value if a different period option is selected
            // Clear the period value if custom period is updated
            if (fieldName == CONSTANTS.DATA_SOURCE_FIELDS.PERIOD.ACCESSOR && value != CONSTANTS.DATA_SOURCE_VALUES.CUSTOM_PERIOD) {
                editingRecord['customPeriod'] = null;
            } else if (fieldName == CONSTANTS.DATA_SOURCE_FIELDS.CUSTOM_PERIOD.ACCESSOR) {
                editingRecord['period'] = null;
            }

            editingRecord[action.payload.fieldName] = action.payload.value;
            
            return {
                ...state,
                loading: false,
                dataSourceRecords: [...state.dataSourceRecords],
            };
        case ACTIONS.TOGGLE_SHOW_DECIMALS.type:
            return {
                ...state,
                showDecimals: !state.showDecimals,
            };
        case ACTIONS.TOGGLE_PULL_BALANCE_BREAKDOWNS.type:
            return {
                ...state,
                pullGLBalancesWithBreakdowns: !state.pullGLBalancesWithBreakdowns,
            };
        case ACTIONS.SHOW_DATA_KEY_SELECTION.type: {
            if (state.recordBeingEdited != null){
                state.recordBeingEdited.showDataKeySelection = true;
                state.showCDTSelector = state.recordBeingEdited.datasource == CONSTANTS.DATA_SOURCE_TYPES.CUSTOM_DATA_TABLE;
            }
            return {
                ...state,
                dataSourceRecords: [...state.dataSourceRecords],
            };
        }
        case ACTIONS.ADD_COMMENT.type: {
            const comment: CalcBuilderComment = action.payload;
            const {dataSourceRecords, commentsHistory, unsavedComments} = state;
            const record = dataSourceRecords.find(x => x.datasourceId == comment.datasourceId);
            if (record != null) {
                commentsHistory.splice(0, 0, comment);
                record.hasComments = true;
                if (comment.ownerIsUnsaved){
                    unsavedComments.push(comment);
                }
            }
            return {
                ...state,
                dataSourceRecords: [...dataSourceRecords],
                commentsHistory: [...commentsHistory],
                newCommentPayload: {...comment, comment: ''},
            };
        }
        case ACTIONS.SET_COA_TYPES.type: {
            return {
                ...state,
                coaTypes: action.payload,
            };
        }
        case ACTIONS.SET_DATA_KEY_INPUT.type: {
            const {key, value} = action.payload;
            const editingRecord = state.recordBeingEdited as any;
            editingRecord['dataKeyInput'][key] = value;
            clearBalancesAndAbandonPullRequest(editingRecord);

            return {
                ...state,
                dataSourceRecords: [...state.dataSourceRecords],
                selectedCustomCoaValues: undefined,
            }
        }
        case ACTIONS.SET_DATA_KEY_COA_INPUT.type: {
            const {key, value} = action.payload;
            const editingRecord = state.recordBeingEdited as any;
            (editingRecord['dataKeyInput']['selectedCOA'] as Map<string, string>).set(key, value);
            clearBalancesAndAbandonPullRequest(editingRecord);
            return {
                ...state,
                dataSourceRecords: [...state.dataSourceRecords],
                selectedCustomCoaValues: undefined,
            }
        }
        case ACTIONS.REMOVE_DATA_KEY_COA_INPUT.type: {
            const key = action.payload;
            const editingRecord = state.recordBeingEdited as any;
            (editingRecord['dataKeyInput']['selectedCOA'] as Map<string, string>).delete(key);
            clearBalancesAndAbandonPullRequest(editingRecord);
            return {
                ...state,
                dataSourceRecords: [...state.dataSourceRecords]
            }
        }
        case ACTIONS.REMOVE_UNUSED_DATA_KEY_COA_INPUTS.type: {
            if (state.recordBeingEdited != undefined && state.recordBeingEdited.dataKeyInput?.selectedCOA != undefined) {
                const coaInputMap = state.recordBeingEdited.dataKeyInput.selectedCOA;
                Array.from(coaInputMap.keys()).forEach(x => { if(coaInputMap.get(x)?.trim() == '') coaInputMap.delete(x); });
            }
            return {
                ...state,
                dataSourceRecords: [...state.dataSourceRecords],
            }
        }
        case ACTIONS.EDIT_COA.type: {
            const editingRecord = (state.recordBeingEdited as any);
            clearBalancesAndAbandonPullRequest(editingRecord);
            return {
                ...state,
                dataSourceRecords: [...state.dataSourceRecords],
            }
        }
        case ACTIONS.UPDATE_CDT_VALUE.type: {
            const {dataSourceId, currency, value} = action.payload as PullCDTValueMessage;
            const records = [...state.dataSourceRecords];

            let { selectedRecord } = getSelectedRecordAndParams({recordID: dataSourceId, value: value}, records);
            if (selectedRecord == null){
                return state;
            }
            
            selectedRecord.value = String(value);
            selectedRecord.currency = currency;
            selectedRecord.lastBalancePullDate = Date.now();
            selectedRecord.balancePullInProgress = false;
            selectedRecord.requestBalancePull = false;

            return {
                ...state,
                dataSourceRecords: records,
            }
        }
        case ACTIONS.UPDATE_MDA_RECORD.type: {
            const { datasourceId, updates } = action.payload;

            const records = [...state.dataSourceRecords];
            const recordIndex = records.findIndex(r => r.datasourceId === datasourceId);
            if (recordIndex === -1) {
                return {...state};
            }
            
            // Update the record with the provided fields
            records[recordIndex] = { ...records[recordIndex], ...updates };
            
            return {
                ...state,
                dataSourceRecords: records,
                recordBeingEdited: records.find((record) => record.datasourceId === state.recordBeingEdited?.datasourceId),
            }
        }
        case ACTIONS.UPDATE_BALANCES.type: {
            const { requestID, totalBalance, calculatedFxRate, ledgerCurrency, glBalances, glBalanceDrillDownKeys } = action.payload;
            const records = [...state.dataSourceRecords];
            
            let { selectedRecord } = getSelectedRecordAndParams(action.payload, records, true);
            if (selectedRecord == null) {
                selectedRecord = getSelectedRecordByRequestID(requestID, records);
            }

            if (selectedRecord == null || isBalancePullAbandoned(selectedRecord, requestID)) {
                return state;
            }
            const record = selectedRecord as DataSourceRecord;
            record.value = String(totalBalance);
            record.fxRate = String(calculatedFxRate);
            record.glBalanceDrillDownKeys = glBalanceDrillDownKeys;
            record.balancePullInProgress = false;
            record.requestBalancePull = false;
            record.errors = undefined;

            if (glBalances == null || glBalances.length == 0) {
                record.glBalances = [{
                    company: record.dataKeyInput?.selectedCompanies || '',
                    account: record.dataKeyInput?.selectedAccounts || '',
                    balance: totalBalance
                }]
            } else {
                record.glBalances = glBalances;
            }

            if (record.balancePullingTask != null) {
                record.lastBalancePullRequestID = record.balancePullingTask.requestId;
            }

            record.lastBalancePullDate = Date.now();

            if (ledgerCurrency != null && ledgerCurrency != '' ) {
                record.currency = ledgerCurrency;
                record.fxType = CONSTANTS.FX_TYPE_PERIOD_AVERAGE;
                const d = endOfMonth(subMonths(startOfMonth(new Date()), 1));
                record.fxDate = format(d, 'yyyy-MM-dd');
            }
            if (record != null && !record.isEditing) {
                record.requestAutoSave = true;
            }

            return {
                ...state,
                balancePullingStateChangeTimestamp: new Date().toISOString(),
                balancePullUpdatedRecord: record?.requestAutoSave? {...record} : undefined,
            }
            
        }
        case ACTIONS.TOGGLE_CATEGORY_EXPANSION.type: {
            const editingRecord = (state.recordBeingEdited as any);
            if (editingRecord['expandedCategories'] == undefined) {
                editingRecord['expandedCategories'] = new Array<string>();
            }
            const expandedCategories = (editingRecord['expandedCategories'] as string[])
            if (expandedCategories.includes(action.payload)) {
                editingRecord['expandedCategories'] = expandedCategories.filter(x => x != action.payload);
            } else {
                expandedCategories.push(action.payload);
            }

            return {
                ...state,
            }
        }
        case ACTIONS.TOGGLE_ACCOUNT_EXPANSION.type: {
            const editingRecord = (state.recordBeingEdited as any);
            if (editingRecord['expandedAccount'] == action.payload) {
                editingRecord['expandedAccount'] = '';
            } else {
                editingRecord['expandedAccount'] = action.payload;
            }

            return {
                ...state,
            }
        }
        case ACTIONS.SELECT_DRILLDOWN_COA.type: {
            const editingRecord = (state.recordBeingEdited as any);
            clearBalanceDrillDown(editingRecord);
            editingRecord['drillDownCOASelected'] = action.payload;
            
            return {
                ...state,
            }
        }
        case ACTIONS.SET_RECORD_ID_FOR_ACTION.type: {
            return {
                ...state,
                recordIdForAction: action.payload,
            }
        }
        case ACTIONS.SET_ERRORS.type: {
            const records = [...state.dataSourceRecords];
            const { value, selectedRecord } = getSelectedRecordAndParams(action.payload, records);
            if (selectedRecord != null) {
                selectedRecord.errors = value;
                return {
                    ...state,
                    dataSourceRecords: records,
                }
            } else {
                return state;
            }
        }
        case ACTIONS.CLEAR_ERRORS.type: {
            const records = [...state.dataSourceRecords];
            const { selectedRecord } = getSelectedRecordAndParams(action.payload, records);
            if (selectedRecord != null) {
                selectedRecord.errors = undefined;
                selectedRecord.saveError = undefined;
                return {
                    ...state,
                    dataSourceRecords: records,
                }
            } else {
                return state;
            }
        }
        case ACTIONS.SHOW_EDIT_CONFIRM_MODAL.type: {
            return {
                ...state,
                showEditActionConfirmModal: true,
            }
        }
        case ACTIONS.HIDE_EDIT_CONFIRM_MODAL.type: {
            return {
                ...state,
                showEditActionConfirmModal: false,
            }
        }
        case ACTIONS.SHOW_DUPLICATE_CONFIRM_MODAL.type: {
            return {
                ...state,
                showDuplicateActionConfirmModal: true,
            }
        }
        case ACTIONS.HIDE_DUPLICATE_CONFIRM_MODAL.type: {
            return {
                ...state,
                showDuplicateActionConfirmModal: false,
            }
        }
        case ACTIONS.SHOW_DELETE_CONFIRM_MODAL.type: {
            return {
                ...state,
                showDeleteActionConfirmModal: true,
            }
        }
        case ACTIONS.HIDE_DELETE_CONFIRM_MODAL.type: {
            return {
                ...state,
                showDeleteActionConfirmModal: false,
            }
        }
        case ACTIONS.SET_NEW_COMMENT_PAYLOAD.type: {
            return {
                ...state,
                newCommentPayload: action.payload,
                // This flag triggers an API call to get comments. We only want to do that for saved data sources
                commentsNeedRefresh: action.payload != null && !(action.payload as CalcBuilderComment).ownerIsUnsaved,
            }
        }
        case ACTIONS.SHOW_STEP3_CONFIRM_MODAL.type: {
            return {
                ...state,
                showStep3ConfirmModal: true,
            }
        }
        case ACTIONS.HIDE_STEP3_CONFIRM_MODAL.type: {
            return {
                ...state,
                showStep3ConfirmModal: false,
            }
        }
        case ACTIONS.ADD_PULL_BALANCE_PAYLOAD.type: {
            const records = [...state.dataSourceRecords];
            const requestPayload = action.payload as PullBalancePayload;
            const { selectedRecord } = getSelectedRecordAndParams({ recordID: requestPayload.payload.dataSourceId }, records);
            if (selectedRecord != null) {
                selectedRecord.unsavedBalancePullRequestID = String(state.pullBalanceRequestCounter);
                const pullBalancePayloads = [...state.pullBalanceRequestPayloads];
                pullBalancePayloads.push(requestPayload);
                
                return {
                    ...state,
                    dataSourceRecords: records,
                    pullBalanceRequestPayloads: pullBalancePayloads,
                    pullBalanceRequestCounter: state.pullBalanceRequestCounter + 1
                }
            } else {
                return state;
            }
        }
        case ACTIONS.REQUEST_BALANCE_PULL.type: {
            const records = [...state.dataSourceRecords];
            const { selectedRecord, value } = getSelectedRecordAndParams(action.payload, records);
            if (selectedRecord != null) {
                if (value && !selectedRecord.balancePullInProgress || !value) {
                    selectedRecord.requestBalancePull = value;
                    if (!selectedRecord.balancePullInProgress) {
                        selectedRecord.balancePullingTask = undefined;
                    }
                }
            }
            return {
                ...state,
                savedRecordId: undefined,
                balancePullingStateChangeTimestamp: new Date().toISOString(),
            }
        }        
        case ACTIONS.REMOVE_PULL_BALANCE_PAYLOAD.type: {
            const payloads = [...state.pullBalanceRequestPayloads];
            payloads.shift();
            return {
                ...state,
                pullBalanceRequestPayloads: payloads,
            }
        }
        case ACTIONS.CLEAR_SAVED_RECORD_ID.type: {
            if (state.recordBeingEdited != null) {
                clearBalanceDrillDown(state.recordBeingEdited);
            }
            return {
                ...state,
                savedRecordId: undefined,
                dataSourceRecords: [...state.dataSourceRecords],
                recordBeingEdited: undefined,
            }
        }
        case ACTIONS.SET_COMMENTS_HISTORY.type: {
            return {
                ...state,
                commentsHistory: action.payload,
            }
        }
        case ACTIONS.CLEAR_UNSAVED_COMMENTS.type: {
            return {
                ...state,
                commentsHistory: [],
                unsavedComments: []
            }
        }
        case ACTIONS.SET_BALANCE_PULL_IN_PROGRESS.type: {
            const records = [...state.dataSourceRecords];
            const { value, selectedRecord } = getSelectedRecordAndParams(action.payload, records);
            if (selectedRecord != null) {
                selectedRecord.balancePullInProgress = value;
                if (value) {
                    selectedRecord.requestBalancePull = false;
                    selectedRecord.pullBalanceError = undefined as unknown as string;
                    selectedRecord.balancePullingTask = undefined;
                    backupBalanceData(selectedRecord);
                    clearBalances(selectedRecord);
                }
                return {
                    ...state,
                    balancePullingStateChangeTimestamp: new Date().toISOString()
                }
            } else {
                return state;
            }
        }
        case ACTIONS.SET_PULL_BALANCE_ERROR.type: {
            const { requestID } = action.payload;
            const records = [...state.dataSourceRecords];
            let { value, selectedRecord } = getSelectedRecordAndParams(action.payload, records, true);

            if (selectedRecord == null) {
                selectedRecord = getSelectedRecordByRequestID(requestID, records);
            }

            if (selectedRecord != null && !isBalancePullAbandoned(selectedRecord, requestID)) {
                selectedRecord.pullBalanceError = value;
                selectedRecord.balancePullInProgress = false;
                if (!selectedRecord.isEditing) {
                    restoreFromBackupBalanceData(selectedRecord);
                }
                return {
                    ...state,
                    balancePullingStateChangeTimestamp: new Date().toISOString()
                }
            } else {
                return state;
            }
        }
        case ACTIONS.SET_BALANCE_DRILL_DOWN.type: {
            const editingRecord = (state.recordBeingEdited as any);
            editingRecord['glBalanceDrillDown'] = action.payload;
            return {
                ...state,
            }
        }
        case ACTIONS.UPDATE_DATASOURCE_RECORD_STATUS.type: {
            const records = [...state.dataSourceRecords];
            const message = action.payload as PullBalanceWSMessage;
            // The initial message from the web socket will return the dataSourceId so we attemp to find the DS record by sataSourceId or unsavedDataSourceId
            let { selectedRecord } = getSelectedRecordAndParams({ recordID: message.dataSourceId }, records, true);
            if (selectedRecord == null) {
                // If record is not found then it means this message is an In Progress status update, those are found by the requestId
                selectedRecord = getSelectedRecordByRequestID(message.requestId, records);
            }
            if (selectedRecord != null){
                selectedRecord.balancePullingTask = message;
                if (selectedRecord.abandonedBalancePullRequests != null 
                    && selectedRecord.abandonedBalancePullRequests.includes(selectedRecord.unsavedBalancePullRequestID)
                    && !selectedRecord.abandonedBalancePullRequests.includes(message.requestId)) {
                    selectedRecord.abandonedBalancePullRequests.push(message.requestId);
                }
                if (message.status.percentage === 100){
                    selectedRecord.balancePullInProgress = false;
                }
            }
            return {
                ...state,
                dataSourceRecords: records,
            }
        }
        case ACTIONS.SHOW_UPDATE_ALL_BALANCES_CONFIRM_MODAL.type: {
            return {
                ...state,
                showUpdateAllBalancesConfirmModal: true,
            }
        }
        case ACTIONS.HIDE_UPDATE_ALL_BALANCES_CONFIRM_MODAL.type: {
            return {
                ...state,
                showUpdateAllBalancesConfirmModal: false,
            }
        }
        case ACTIONS.SET_SHOW_CDT_SELECTOR.type: {
            return {
                ...state,
                showCDTSelector: action.payload,
            }
        }
        case ACTIONS.PROCESS_NEXT_BALANCE_PULL_REQUEST.type: {
            const records = [...state.dataSourceRecords];
            let selectedRecord = null;
            let startIndex = 0, skippedRecordsCount = 0, recordsCountEligibleForPullingBalance = 0;
            if (action.payload != null) {
                selectedRecord = getSelectedRecordAndParams(action.payload, records).selectedRecord;
                if (selectedRecord != null) {
                    startIndex = records.indexOf(selectedRecord) + 1;
                    while(startIndex < records.length) {
                        selectedRecord = records[startIndex];
                        if (selectedRecord.datasource === CONSTANTS.DATA_SOURCE_TYPES.TP_ALLOCATION_WORKSHEET){
                            startIndex++;
                            skippedRecordsCount++;
                        }
                        else {
                            break;
                        }
                    }
                }
            }
            else {
                recordsCountEligibleForPullingBalance = records.filter(x => x.datasource !== CONSTANTS.DATA_SOURCE_TYPES.TP_ALLOCATION_WORKSHEET).length;
            }
            let index = -1;
            for (let i = startIndex; i < records.length; i++) {
                const record = records[i];
                if (!record.balancePullInProgress && record.datasource !== CONSTANTS.DATA_SOURCE_TYPES.TP_ALLOCATION_WORKSHEET) {
                    index = i;
                    break;
                }
            }
            if (index != -1) {
                records[index].requestBalancePull = true;
                if (action.payload == null) {
                    return {
                        ...state,
                        updateAllBalances: true,
                        balancePullingStateChangeTimestamp: new Date().toISOString(),
                        recordsCountEligibleForPullingBalance: recordsCountEligibleForPullingBalance
                    }
                } else {
                    return {
                        ...state,
                        balancePullingStateChangeTimestamp: new Date().toISOString(),
                        pullBalanceRequestCounter: state.pullBalanceRequestCounter + skippedRecordsCount,                        
                    }
                }
            } else {
                return {
                    ...state,
                    updateAllBalances: false,
                    balancePullingStateChangeTimestamp: undefined,
                    recordsCountEligibleForPullingBalance: 0
                }   
            }
        }
        case ACTIONS.TOGGLE_DATA_KEY_VIEW.type: {
            const records = [...state.dataSourceRecords];
            const { selectedRecord } = getSelectedRecordAndParams(action.payload, records);
            if (selectedRecord != null) {
                if (selectedRecord == state.recordBeingEdited) {
                    selectedRecord.showDataKeySelection = false;
                    clearBalanceDrillDown(selectedRecord);
                    return {
                        ...state,
                        recordBeingEdited: undefined,
                        dataSourceRecords: records,
                        showCDTSelector: false
                    }
                } else {
                    if (state.recordBeingEdited != null) {
                        clearBalanceDrillDown(state.recordBeingEdited);
                    }
                    selectedRecord.showDataKeySelection = true;
                    return {
                        ...state,
                        recordBeingEdited: selectedRecord,
                        dataSourceRecords: records,
                        showCDTSelector: selectedRecord.datasource == CONSTANTS.DATA_SOURCE_TYPES.CUSTOM_DATA_TABLE,
                    }
                }
            } else {
                return state;
            }
        }
        case ACTIONS.SET_FX_INPUTS.type: {
            const { recordBeingEdited } = state;
            // Only currency can be provided - FX type is defaulted to 'Period Average' and FX date is defaulted to previous month end date
            // Only currency and FX type 'Period Average' or 'Period End' can be provided - the FX date is defaulted to previous month end date
            // For STAT currency, FX date and type provided are discarded
            // In all other cases, all 3 inputs are required - this is checked during validation in the DataSourcesService
            // If a non-month end date is provided for FX type 'Period Average' or 'Period End', it is set to the month end of the date provided
            if (recordBeingEdited?.currency != null) {
                if (recordBeingEdited.currency == CONSTANTS.STAT_CURRENCY) {
                    (recordBeingEdited as any).fxDate = undefined;
                    (recordBeingEdited as any).fxType = undefined;

                } else {
                    if (StringUtils.isNullOrEmpty(recordBeingEdited.fxType) 
                        || ((recordBeingEdited.fxType == CONSTANTS.FX_TYPE_PERIOD_AVERAGE || recordBeingEdited.fxType == CONSTANTS.FX_TYPE_PERIOD_END)
                            && StringUtils.isNullOrEmpty(recordBeingEdited.fxDate))) {
                        if (StringUtils.isNullOrEmpty(recordBeingEdited.fxType)) {
                            recordBeingEdited.fxType = CONSTANTS.FX_TYPE_PERIOD_AVERAGE;
                        }
                        const d = endOfMonth(subMonths(startOfMonth(new Date()), 1));
                        recordBeingEdited.fxDate = format(d, 'yyyy-MM-dd');
                    } else if (recordBeingEdited.fxType == CONSTANTS.FX_TYPE_PERIOD_AVERAGE || recordBeingEdited.fxType == CONSTANTS.FX_TYPE_PERIOD_END) {
                        const d = endOfMonth(Date.parse(recordBeingEdited.fxDate));
                        recordBeingEdited.fxDate = format(d, 'yyyy-MM-dd');
                    }
                }
            }
            return {
                ...state,
                dataSourceRecords: [...state.dataSourceRecords],
            }
        }
        case ACTIONS.SET_SELECTED_CUSTOM_COA.type: {
            const { valueMap, referenceMap } = action.payload;
            const existingReferenceMap = state.customCoaReference || new Map<string, string>();
            (referenceMap as Map<string, string>).forEach((value: string, key: string) => existingReferenceMap.set(key, value));

            return {
                ...state,
                selectedCustomCoaValues: valueMap,
                customCoaReference: existingReferenceMap,
            }
        }
        case ACTIONS.SET_CUSTOM_COA_REFERENCE.type: {
            return {
                ...state,
                customCoaReference: action.payload,
            }
        }
        case ACTIONS.SET_SAVE_ERROR.type: {
            const { recordBeingEdited } = state;
            if (recordBeingEdited != null) {
                recordBeingEdited.saveError = action.payload;
            }
            return {
                ...state,
                dataSourceRecords: [...state.dataSourceRecords],
            }
        }
        case ACTIONS.SHOW_STANDARD_ALLOCATION_MODAL.type: {
            return {
                ...state,
                showStandardAllocationModal: action.payload,
            }
        }
        case ACTIONS.SHOW_STANDARD_ALLOCATION_DELETE_CONFIRM_MODAL.type: {
            return {
                ...state,
                showStandardAllocationDeleteConfirmModal: true,
            }
        }
        case ACTIONS.HIDE_STANDARD_ALLOCATION_DELETE_CONFIRM_MODAL.type: {
            return {
                ...state,
                showStandardAllocationDeleteConfirmModal: false,
            }
        }
        case ACTIONS.CLEAR_ALL_BALANCE_PULL_REQUESTS.type: {
            const {dataSourceRecords} = state;
            dataSourceRecords.forEach(r => {
                r.balancePullInProgress = false;
                r.requestBalancePull = false;
            })
            return {
                ...state,
                pullBalanceRequestCounter: 0,
                pullBalanceRequestPayloads: [],
                balancePullingStateChangeTimestamp: undefined,
                balancePullUpdatedRecord: undefined,
                showUpdateAllBalancesConfirmModal: false,
                recordsCountEligibleForPullingBalance: 0,
                dataSourceRecords: [...dataSourceRecords]
            }
        }
        case ACTIONS.DELETE_STANDARD_ALLOCATION_RECORD.type: {
            const record = state.dataSourceRecords.find(x => x.datasourceId == state.recordIdForAction);
            if (record != null) {
                record.standardAllocation = undefined;
            }
            return {
                ...state,
                dataSourceRecords: [...state.dataSourceRecords],
            };
        }
        case ACTIONS.TOGGLE_PASS_THROUGH_FLAG.type: {
            const record = state.dataSourceRecords.find(x => x.datasourceId == state.recordIdForAction);
            if (record != null) {
                if (record.passThroughFlag) {
                    record.passThroughFlag = false;
                } else {
                    record.passThroughFlag = true;
                }
                return {
                    ...state,
                    dataSourceRecords: [...state.dataSourceRecords],
                    recordForActionNeedsSaving: (new Date()).toString(),
                };
            }
            return state;
        }
        default:
            console.warn(`No action found for ${action.type}. Returning unchanged state`)
            return state;
    }
}
